import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/** Build the page number array with ellipsis markers ('...') */
function buildPages(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [];

  // Always show first page
  pages.push(1);

  if (current > 3) {
    pages.push('...');
  }

  // Pages around current
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 2) {
    pages.push('...');
  }

  // Always show last page
  pages.push(total);

  return pages;
}

export default function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const { t } = useLanguage();

  if (totalPages <= 1) return null;

  const pages = buildPages(currentPage, totalPages);
  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  return (
    <div className="flex items-center justify-center gap-1.5 py-3 px-4">

      {/* Mobile: compact prev/label/next */}
      <div className="flex sm:hidden items-center gap-3 w-full justify-between">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={isFirst}
          className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
            isFirst
              ? 'opacity-50 pointer-events-none text-gray-400 bg-gray-100'
              : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'
          }`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t('tracker.pagePrev')}
        </button>
        <span className="text-xs text-gray-500 font-medium">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLast}
          className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
            isLast
              ? 'opacity-50 pointer-events-none text-gray-400 bg-gray-100'
              : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'
          }`}
        >
          {t('tracker.pageNext')}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Desktop: full number chain */}
      <div className="hidden sm:flex items-center gap-1">
        {/* Prev button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={isFirst}
          className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
            isFirst
              ? 'opacity-50 pointer-events-none text-gray-400 bg-gray-100'
              : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'
          }`}
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t('tracker.pagePrev')}
        </button>

        {/* Page numbers */}
        {pages.map((page, idx) =>
          page === '...' ? (
            <span key={`dots-${idx}`} className="w-9 h-9 flex items-center justify-center text-xs text-gray-400 select-none">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-9 h-9 rounded-xl text-xs font-bold transition-colors ${
                page === currentPage
                  ? 'bg-goszakup-600 text-white shadow-sm shadow-goszakup-600/30'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {page}
            </button>
          )
        )}

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLast}
          className={`flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
            isLast
              ? 'opacity-50 pointer-events-none text-gray-400 bg-gray-100'
              : 'text-goszakup-600 bg-goszakup-50 hover:bg-goszakup-100'
          }`}
        >
          {t('tracker.pageNext')}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
